import { log } from '@/log'
import ApiBrand from '@/models/api/brand'
import { ProductDetail } from '@/models/api/cart/cart/product-detail'
import { ReplaceCartPriceParameter, ReplacePriceRepository } from '@/models/api/cart/latest-price/replace-price-reposiory'
import { ValidPrice } from '@/models/api/cart/latest-price/scheme'
import { ApiProduct } from '@/models/api/product'
import { isPaidMembershipPrice } from '@/models/item/sku'
import { EnableToListCartItem } from '@spa/store/modules/cart/repository'
import { CartItem } from '@spa/store/modules/cart/types'
import _ from 'lodash'
import { ReasonForCannotBePurchasedList, ShouldPromoteCountSelection } from './types/reason-for-cannot-be-purchased'

export interface CanConvertRawItem<T = any> {
  convert(rawDataList: T[]): T extends CartItem ? never : Promise<CartItem[]>
}

export class RawItemConverter implements CanConvertRawItem {
  static create(): RawItemConverter {
    return new RawItemConverter()
  }
  private static convertToCartItem(rawItem): CartItem {
    /**
     * 将来的にはproductの参照自体をやめて、product detailのみ参照すればよいようにする
     */
    const apiProduct = new ApiProduct(_.get(rawItem, 'product'))
    const belongingBrand = new ApiBrand(rawItem.brands[0])
    const productDetail = ProductDetail.safeParse(rawItem.productDetail)
    const root = productDetail.root

    return {
      id: rawItem.id,
      name: productDetail.getRootProductName(''),
      createdAt: _.get(rawItem, 'created_at'),
      unitPrice: _.get(rawItem, 'unitPrice'),
      /**
       * TODO: 一元管理する
       * 現状で @/models/cart/repository/impl/cart-item/engine-cart-item-repository にもベタ書きされている
       */
      isWithPaidMembershipPrice: _.get(rawItem, 'aux_string_1') === 'with-paid-member-price',
      skuSellPrice: apiProduct.price.regular,
      skuSalePrice: apiProduct.price.discounted,
      unitCount: _.get(rawItem, 'unitCount') as number,
      brandId: belongingBrand.id,
      brandName: belongingBrand ? belongingBrand.name : null,
      brandEnglishName: belongingBrand ? belongingBrand.englishName : null,
      skuId: apiProduct.id,
      skuShortId: apiProduct.shortId,
      skuNumber: apiProduct.skuNumber,
      fkuId: apiProduct.productClassId,
      size: apiProduct.getSize(),
      sizeTagPathname: apiProduct.getSizeTagPathname(),
      onSale: apiProduct.isOnDiscountSale,
      salesStatus: _.get(rawItem, 'product.sales_status') as number,
      /**
       * ProductDetailに情報があればそれを採用、なければカートのproduct（をパースしたApiProduct）を採用する。
       *
       * 事情:
       * - ProductDetailがなくてもカートのproductは存在することがある（Engine APIがproductを返す場合）
       * - 既存の挙動を維持するため、ProductDetailに情報がない場合はApiProductから取得する
       * - ProductDetailの内部データ型を外部に秘匿するため、getSalesStartAtOrElse/getSalesEndAtOrElseを使用
       */
      salesStartAt: productDetail.getSalesStartAtOrElse(apiProduct.regularSaleStartAt),
      salesEndAt: productDetail.getSalesEndAtOrElse(apiProduct.regularSaleEndAt),
      isPreSales: productDetail.isPreSale,
      isParent: productDetail.isParent,
      isChild: productDetail.isChild,
      isNovelty: productDetail.isNovelty,
      /**
       * FKU情報が取得できなかった（/productclass2の結果が取得できなかった）場合、falseになる。
       * productDetailはFKUが取れなくてもSKUが取れれば返ってくるため、FKU情報の有無で判定する。
       *
       * NOTE: このフラグ(isFkuPublic)は場当たり的な実装（後からjoinするのでそういう情報をフラグで持たなければならなかった）であり、
       * 将来的には廃止したい。ProductDetail 側に「情報は全部揃っている」みたいなbooleanを返すメソッドを
       * 作るのが良いが、今は既存のデータ構造（storeのtype）を変えないことにしたので手を入れていない。
       */
      isFkuPublic: productDetail.hasFku,
      childItem: root ? root.usingTo.find(relatedItem => relatedItem.isChild) || null : null,
      ineligibleForCoupon: productDetail.hasRootTag('mycolor.product.ineligible.coupon'),
      freeShipping: productDetail.hasRootTag('mycolor.product.delivery.free_shipping'),
      categories: root ? root.categories : [],
      reasonForCannotBePurchased: ReasonForCannotBePurchasedList.asEmpty(),
      thumbnail: productDetail.thumbnail || undefined,
      color: productDetail.colorLabel || undefined,
      colorName: productDetail.colorName || undefined,
      root: root || undefined,
      rootId: root ? root.id : undefined,
      rootShortId: root ? root.shortId : undefined,
      rootPurchaseLimit: root ? root.purchaseLimit : undefined,
      maxSellCount: productDetail.maxSellCount != null ? productDetail.maxSellCount : undefined,
      stock: productDetail.stock != null ? productDetail.stock : undefined,
      stockAppealText: productDetail.stockAppealText != null ? productDetail.stockAppealText : undefined,
      canRequestRestock: productDetail.canRequestRestock != null ? productDetail.canRequestRestock : undefined,
      isCatalogType: productDetail.hasRootTag('mycolor.product.catalog'),
    }
  }

  private constructor() {}

  async convert(rawDataList: any[]): Promise<CartItem[]> {
    return this.convertToCartItems(rawDataList)
  }

  convertToCartItems(responseData: any[]): CartItem[] {
    return _.map(responseData, rawItem => RawItemConverter.convertToCartItem(rawItem))
  }
}

export class ConvertWithBeforePrice {
  private replaceListeners: Array<() => void> = []

  constructor(
    private readonly repository: EnableToListCartItem,
    private readonly replacePriceRepository: ReplacePriceRepository
  ) {}

  onReplaced(listener: () => void): this {
    this.replaceListeners.push(listener)

    return this
  }

  async convert(baseItems: CartItem[]): Promise<CartItem[]> {
    const maybeValidPriceList = await this.replacePriceRepository.updateCurrentValidPrice()
    if (maybeValidPriceList.isLeft()) {
      const error = maybeValidPriceList.value
      log.error('Could not fetch latest cart item price', maybeValidPriceList.value)
      throw error
    }

    const validPriceList = maybeValidPriceList.value

    validPriceList.failure.forEach((failure) => {
      const failedCart = baseItems.find(i => i.id === failure.cart_id)

      if (!failedCart) return

      failedCart.reasonForCannotBePurchased = failedCart.reasonForCannotBePurchased.add(
        {
          type: 'priceDifferenceNotChecked',
          errors: failure.errors,
          message: failure.message || '価格の取得に失敗したため現在ご購入いただけません。',
        }
      )
    })

    /**
     * まだ会員価格での購入残数があるにも関わらず非会員価格が選択されている、
     * すなわち上限を超えてカートに入れられている場合、非有料会員価格を適用するのではなく、
     * 購入上限数を超過しているため会員価格を適用できないことをエラーとして表示する。
     * Ledian v1としては削除してから再度カート追加してもらうが、
     * 本来は有料会員価格が適用できる範囲で個数を選択できるようなセレクトボックスを表示することが表示されているべき。
     *
     * また、価格に関して要求とコードが乖離してきているのでユースケースを整理することが必要そう。
     * このままだと/api/user/cart/current_valid_priceが謎の巨大便利APIになってしまう。
     */
    validPriceList.success.forEach((success) => {
      const selectedPriceIsNonPaidMemberPrice = !success.current_valid_price.prices.selected.is_paid_membership_price
      const unselectedPricesRemainingCountIsExist = _.filter(
        success.current_valid_price.prices.unselected,
        price => price.rest_amount.add_to_cart > 0
      )

      if (selectedPriceIsNonPaidMemberPrice && unselectedPricesRemainingCountIsExist.length > 0) {
        const cart = baseItems.find(i => i.id === success.cart_id)
        if (!cart) return

        cart.reasonForCannotBePurchased = cart.reasonForCannotBePurchased.add({
          type: 'shouldPromoteCountSelection',
          errors: ['non-paid-member price was applied despite the number of remaining amount of paid-member price exists.'],
          message: '購入上限数を超えています。数量を変更してください。',
          count: {
            min: 1,
            max: _.max(unselectedPricesRemainingCountIsExist.map(p => p.rest_amount.add_to_cart)) || 1,
          },
        } as ShouldPromoteCountSelection)
      }
    })

    // 現在のセール状態や会員状態に応じた最新の有効価格と比較して、カート内価格が違っていれば強制変更する。
    const replaceCartPriceParameterList = _.compact(
      baseItems.map((cartItem): ReplaceCartPriceParameter | null => {
        const validPrice = _.find(validPriceList.success, d => d.cart_id === cartItem.id)

        /**
         * 対応するSKU最新有効価格が取得できなかった場合、カート取得処理そのものを止めるよりは、
         * ユーザが購買プロセスを続行できることを優先し、warningに留めている。
         *
         * ただし最終的には以下の議論から、SKU最新有効価格が取得できなかった部分に対してのみ、何らかの非整合性フラグを立てて、
         * 非整合なデータにふさわしい処理を別途行うようにする予定である。
         * @see https://github.com/my-color/front/pull/6011#discussion_r783369523
         */
        if (!validPrice) {
          log.warn(`Could not fetch latest price for cart: ${cartItem.id})`)

          return null
        }

        if (!validPrice.difference) {
          return null
        }

        return {
          cartId: cartItem.id,
          skuId: cartItem.skuId,
          unitPrice: validPrice.current_valid_price.prices.selected.price,
          isPaidMembershipPrice: isPaidMembershipPrice(validPrice.current_valid_price),
          unitCount: cartItem.unitCount,
        }
      })
    )

    if (_.isEmpty(replaceCartPriceParameterList)) {
      return this.joinCurrentValidPrice(baseItems, validPriceList)
    }

    // valid_price取得時にカートの更新がされているので再度取得する
    const updated = await this.repository.list()
    const latestPriceJoined = this.joinCurrentValidPrice(updated, validPriceList)

    this.replaceListeners.forEach(l => l())

    const incorrectPriceItems = baseItems.filter((item) => {
      return !!_.find(replaceCartPriceParameterList, param => param.cartId === item.id)
    })

    return this.setBeforePrice(incorrectPriceItems, latestPriceJoined)
  }

  setBeforePrice(staleItems: CartItem[], items: CartItem[]): CartItem[] {
    // check change before price in cart
    _.forEach(staleItems, (staleItem) => {

      const item = _.find(items, ['skuId', staleItem.skuId])

      if (!item) {
        return
      }

      if (staleItem.unitPrice === item.unitPrice || item.removed) {
        return
      }

      item.beforePrice = staleItem.unitPrice
    })

    return items
  }

  private joinCurrentValidPrice(cartItems: CartItem[], validPriceList: ValidPrice): CartItem[] {
    return cartItems.map((cartItem: CartItem): CartItem => {
      const validPrice = _.find(validPriceList.success, d => d.cart_id === cartItem.id)

      /**
       * 対応するSKU最新有効価格が取得できなかった場合、カート取得処理そのものを止めるよりは、
       * ユーザが購買プロセスを続行できることを優先し、warningに留めている。
       */
      if (!validPrice) {
        log.warn(`Could not fetch latest price for cart: ${cartItem.id})`)

        return cartItem
      }

      return {
        ...cartItem,
        currentValidPrice: validPrice.current_valid_price,
      }
    })
  }
}
